import React, { useState } from 'react'
import {Send, CheckCircle} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

export default function LeadForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    serviceInterest: '',
    projectDescription: '',
    budget: '',
    timeline: ''
  })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const leadData = {
        ...formData,
        status: 'new',
        submittedAt: new Date().toISOString()
      }
      
      // Save lead to database
      await lumi.entities.leads.create(leadData)
      
      // Send email notification
      try {
        await lumi.functions.invoke('notifyLead', {
          method: 'POST',
          body: { leadData }
        })
      } catch (emailError) {
        console.error('Email notification error:', emailError)
        // Continue even if email fails - lead is already saved
      }
      
      setSubmitted(true)
      toast.success('Thank you! We\'ll contact you soon.')
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          serviceInterest: '',
          projectDescription: '',
          budget: '',
          timeline: ''
        })
        setSubmitted(false)
      }, 3000)
    } catch (error) {
      console.error('Lead submission error:', error)
      toast.error('Failed to submit. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  if (submitted) {
    return (
      <div className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-slate-800/50 border border-emerald-500/50 rounded-2xl p-12">
            <CheckCircle className="w-20 h-20 text-emerald-400 mx-auto mb-6" />
            <h3 className="text-3xl font-bold text-white mb-4">Thank You!</h3>
            <p className="text-xl text-gray-300">
              We've received your information and will contact you shortly to discuss how we can help transform your business.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Ready to Transform Your<br />
            <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              Project Outcomes?
            </span>
          </h2>
          <p className="text-xl text-gray-400">
            Schedule a free consultation to discuss your project needs and discover how we can help
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-300 mb-2 font-medium">Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
                placeholder="john@company.com"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
                placeholder="(919) 123-4567"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">Company *</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
                placeholder="Your Company Name"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">Service Interest *</label>
              <select
                name="serviceInterest"
                value={formData.serviceInterest}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
              >
                <option value="">Select a service</option>
                <option value="AI Consultation">AI Consultation</option>
                <option value="Business Process Optimization">Business Process Optimization</option>
                <option value="Change Management">Change Management</option>
                <option value="Project Management">Project Management</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">Timeline</label>
              <select
                name="timeline"
                value={formData.timeline}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
              >
                <option value="">Select timeline</option>
                <option value="Immediate">Immediate (Within 1 month)</option>
                <option value="1-3 months">1-3 months</option>
                <option value="3-6 months">3-6 months</option>
                <option value="6+ months">6+ months</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-gray-300 mb-2 font-medium">Budget Range</label>
            <select
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition"
            >
              <option value="">Select budget range</option>
              <option value="Unknown">Unknown</option>
              <option value="Under $1K">Under $1K</option>
              <option value="Over $1K">Over $1K</option>
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-gray-300 mb-2 font-medium">Project Description *</label>
            <textarea
              name="projectDescription"
              value={formData.projectDescription}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition resize-none"
              placeholder="Tell us about your project needs and goals..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-xl hover:shadow-cyan-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <span>Submit Consultation Request</span>
                <Send className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
